/**
 * AgentBill Vercel AI SDK Provider Wrapper
 */

import type { AgentBillConfig, SignalPayload, TrackingMetadata } from './types';

export class AgentBillVercel {
  private config: AgentBillConfig;
  private signalQueue: SignalPayload[] = [];
  private lastFlush: number = Date.now();
  private flushTimer?: NodeJS.Timeout;

  constructor(config: AgentBillConfig) {
    this.config = {
      baseUrl: 'https://bgwyprqxtdreuutzpbgw.supabase.co',
      batchSize: 10,
      flushInterval: 5000,
      debug: false,
      ...config
    };

    // Start flush timer
    this.startFlushTimer();

    if (this.config.debug) {
      console.log(`[AgentBill] Initialized with baseUrl=${this.config.baseUrl}`);
    }
  }

  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      if (this.signalQueue.length > 0) {
        this.flush();
      }
    }, this.config.flushInterval);
  }

  private hashPrompt(text: string): string {
    // Simple hash for browser/node compatibility
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
  }

  private extractProvider(modelId: string): string {
    const lower = modelId.toLowerCase();
    if (lower.includes('gpt') || lower.includes('openai')) return 'openai';
    if (lower.includes('claude') || lower.includes('anthropic')) return 'anthropic';
    if (lower.includes('gemini') || lower.includes('google')) return 'google';
    if (lower.includes('llama') || lower.includes('meta')) return 'meta';
    if (lower.includes('mistral')) return 'mistral';
    return 'unknown';
  }

  private async sendSignals(signals: SignalPayload[]): Promise<void> {
    try {
      const url = `${this.config.baseUrl}/functions/v1/record-signals`;
      const headers = {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey
      };

      if (this.config.debug) {
        console.log(`[AgentBill] Sending ${signals.length} signals`);
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(signals)
      });

      if (!response.ok) {
        const text = await response.text();
        console.error(`[AgentBill] Error sending signals: ${response.status} ${text}`);
      } else if (this.config.debug) {
        console.log('[AgentBill] Signals sent successfully');
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('[AgentBill] Error sending signals:', error);
      }
    }
  }

  private queueSignal(signal: SignalPayload): void {
    this.signalQueue.push(signal);

    // Auto-flush if batch size reached
    if (this.signalQueue.length >= (this.config.batchSize || 10)) {
      this.flush();
    }
  }

  /**
   * Flush queued signals to AgentBill
   */
  async flush(): Promise<void> {
    if (this.signalQueue.length === 0) return;

    const signals = [...this.signalQueue];
    this.signalQueue = [];
    this.lastFlush = Date.now();

    await this.sendSignals(signals);
  }

  /**
   * Wrap an AI SDK provider for automatic tracking
   * 
   * @example
   * const trackedOpenAI = agentBill.wrapProvider(openai);
   * const result = await generateText({
   *   model: trackedOpenAI('gpt-4o-mini'),
   *   prompt: 'Hello!'
   * });
   */
  wrapProvider<T extends (...args: any[]) => any>(provider: T): T {
    const self = this;

    return new Proxy(provider, {
      apply(target, thisArg, args) {
        // Provider is called with model ID, e.g., openai('gpt-4o-mini')
        const modelId = args[0] as string;
        const originalModel = Reflect.apply(target, thisArg, args);

        // Wrap the model object
        return self.wrapModel(originalModel, modelId);
      }
    }) as T;
  }

  private wrapModel(model: any, modelId: string): any {
    const self = this;
    const provider = this.extractProvider(modelId);

    return new Proxy(model, {
      get(target, prop) {
        const original = target[prop];

        // Intercept doGenerate and doStream methods
        if (prop === 'doGenerate' || prop === 'doStream') {
          return async function(...args: any[]) {
            const startTime = Date.now();
            const [params] = args;

            // Extract prompt
            let prompt = '';
            if (params.prompt) {
              if (Array.isArray(params.prompt)) {
                prompt = params.prompt.map((p: any) => 
                  typeof p === 'string' ? p : JSON.stringify(p)
                ).join('\n');
              } else {
                prompt = typeof params.prompt === 'string' 
                  ? params.prompt 
                  : JSON.stringify(params.prompt);
              }
            }

            const promptHash = prompt ? self.hashPrompt(prompt) : undefined;
            const promptSample = prompt.substring(0, 200);

            try {
              // Call original method
              const result = await original.apply(target, args);

              // Calculate latency
              const latencyMs = Date.now() - startTime;

              // Extract token usage
              const usage = result.usage || {};
              const promptTokens = usage.promptTokens || 0;
              const completionTokens = usage.completionTokens || 0;
              const totalTokens = usage.totalTokens || promptTokens + completionTokens;

              // Extract event_name from params if provided
              const eventName = params.agentbill?.event_name || 'vercel_ai_call';
              
              // Build signal
              const signal: SignalPayload = {
                event_name: eventName,
                model: modelId,
                provider,
                prompt_hash: promptHash,
                prompt_sample: promptSample,
                prompt_tokens: promptTokens,
                completion_tokens: completionTokens,
                total_tokens: totalTokens,
                latency_ms: latencyMs,
                data_source: 'vercel_ai',
                // OTEL correlation - optional trace_id and parent_span_id
                trace_id: params.agentbill?.trace_id,
                parent_span_id: params.agentbill?.span_id // Maps span_id to parent_span_id
              };

              // Add customer/account/agent if provided
              // If customerId is a UUID, send as customer_id, otherwise as customer_external_id
              if (self.config.customerId) {
                const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(self.config.customerId);
                if (isUuid) {
                  (signal as any).customer_id = self.config.customerId;
                } else {
                  signal.customer_external_id = self.config.customerId;
                }
              }
              if (self.config.accountId) {
                signal.account_external_id = self.config.accountId;
              }
              if (self.config.agentId) {
                signal.agent_external_id = self.config.agentId;
              }

              // Queue signal
              self.queueSignal(signal);

              if (self.config.debug) {
                console.log(`[AgentBill] AI call tracked: ${totalTokens} tokens, ${latencyMs}ms`);
              }

              return result;
            } catch (error) {
              if (self.config.debug) {
                console.error('[AgentBill] AI call error:', error);
              }
              throw error;
            }
          };
        }

        return original;
      }
    });
  }

  /**
   * Track custom revenue for profitability analysis
   * 
   * @example
   * agentBill.trackRevenue({
   *   event_name: 'ai_completion',
   *   revenue: 0.50,
   *   metadata: { subscription: 'pro' }
   * });
   */
  trackRevenue(params: {
    event_name: string;
    revenue: number;
    metadata?: Record<string, any>;
  }): void {
    const signal: SignalPayload = {
      event_name: params.event_name,
      conversion_value: params.revenue,
      revenue_source: 'vercel_ai',
      data_source: 'vercel_ai',
      data: params.metadata
    };

    // If customerId is a UUID, send as customer_id, otherwise as customer_external_id
    if (this.config.customerId) {
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(this.config.customerId);
      if (isUuid) {
        (signal as any).customer_id = this.config.customerId;
      } else {
        signal.customer_external_id = this.config.customerId;
      }
    }
    if (this.config.accountId) {
      signal.account_external_id = this.config.accountId;
    }

    this.queueSignal(signal);

    if (this.config.debug) {
      console.log(`[AgentBill] Revenue tracked: $${params.revenue}`);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}
